import React from 'react';
import {
  InputSwitch,
  type InputSwitchChangeEvent,
} from 'primereact/inputswitch';
import { ReactComponent as ChevronDownIcon } from '../../../../assets/ic_chevron_down.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/ic_info.svg';

interface IFeatureProps extends React.PropsWithChildren {
  icon: string;
  image: string;
  title: string;
  description: string;
  isMandatory?: boolean;
  checked: boolean;
  onChange?: (checked: boolean) => void;
}

export default function Feature({
  icon,
  image,
  title,
  description,
  isMandatory,
  checked,
  onChange,
  children,
}: IFeatureProps) {
  const [collapsed, setCollapsed] = React.useState(true);

  function handleOnChange(event: InputSwitchChangeEvent) {
    onChange?.(event.value ?? false);
  }

  function toggleCollapsed() {
    setCollapsed((prev) => !prev);
  }

  return (
    <div className="flex flex-col px-6 pt-5 bg-white rounded-3xl shadow-md max-md:px-5 mt-4">
      <div className="flex gap-5 justify-between w-full max-md:flex-wrap max-md:max-w-full">
        <div className="flex gap-3 items-center">
          <img src={icon} className="shrink-0 self-stretch w-8 aspect-square" />

          <div className="self-stretch my-auto text-xl font-semibold leading-8 text-black">
            {title}
          </div>
          <InputSwitch
            checked={checked}
            onChange={handleOnChange}
            disabled={isMandatory}
          />
        </div>
        <button
          className="flex flex-1 justify-end px-4"
          onClick={toggleCollapsed}
        >
          <ChevronDownIcon
            className={`shrink-0 my-auto w-6 aspect-square stroke-cyan-700 duration-[0.3s] ${collapsed ? '' : 'rotate-180'}`}
          />
        </button>
      </div>

      <div
        className={`overflow-hidden mt-2 transition-[max-height] duration-300 ${collapsed ? 'max-h-0' : 'max-h-[1000rem]'}`}
      >
        <div className="shrink-0 h-px w-[80%] border-b border-solid bg-zinc-300 border-zinc-300 max-md:max-w-full" />
        <div className="max-md:max-w-full">
          <div className="flex gap-5 max-md:flex-col max-md:gap-0">
            <div className="flex flex-col w-[80%] max-md:ml-0 max-md:w-full">
              <div className="flex gap-2 mt-4 text-xl tracking-wide font-medium leading-7 text-blue-700 max-md:flex-wrap max-md:mt-10">
                <InfoIcon className="shrink-0 self-start h-8 aspect-square stroke-[color:var(--color-darkGray)]" />
                <div className="max-md:max-w-full text-[color:var(--color-stormGray)]">
                  {description}
                  {isMandatory && (
                    <>
                      <br />
                      <br />
                      <span className="text-blue-700">
                        This feature is mandatory and cannot be disabled.
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-center px-16 w-[15%] max-md:ml-0 max-md:w-full">
              <img
                src={require(`../../../../assets/${image}.png`)}
                className="grow max-w-full aspect-[0.53] w-[145px] max-md:mt-10"
              />
            </div>
          </div>
        </div>

        {children}
      </div>
    </div>
  );
}
